/**
 * Dashboard charts (M31, Recharts). Each chart is an image with a summary
 * name, and its figures are also given as a table for screen readers, so
 * nothing is shown by colour or shape alone (WCAG 1.1.1, 1.4.1).
 */
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { Dashboard } from "@/api/dashboard";

const AXIS = { fontSize: 12, fill: "var(--muted-foreground)" };
const TOOLTIP = {
  contentStyle: {
    background: "var(--popover)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    fontSize: 12,
    color: "var(--foreground)",
  },
  cursor: { fill: "var(--muted)" },
};

function monthName(key: string, language: string): string {
  const [year, month] = key.split("-").map(Number);
  return new Intl.DateTimeFormat(language === "en" ? "en-IN" : language, {
    month: "short",
  }).format(new Date(year ?? 2000, (month ?? 1) - 1, 1));
}

/** Submissions and decisions in each of the last six months. */
export function MonthlyChart({ months }: { months: Dashboard["perMonth"] }) {
  const { t, i18n } = useTranslation();
  const data = months.map((row) => ({ ...row, name: monthName(row.month, i18n.language) }));
  const series = [
    ["submitted", "var(--chart-1)"],
    ["approved", "var(--chart-3)"],
    ["returned", "var(--chart-4)"],
    ["rejected", "var(--chart-5)"],
  ] as const;
  return (
    <figure className="flex flex-col gap-2">
      <div role="img" aria-label={t("dashboard.monthlySummary")} className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }} barGap={2}>
            <CartesianGrid vertical={false} stroke="var(--border)" />
            <XAxis dataKey="name" tickLine={false} axisLine={false} tick={AXIS} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={AXIS} />
            <Tooltip {...TOOLTIP} />
            {/* The words in the text colour: a series' colour alone can be too light to read. */}
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} formatter={(value: string) => <span style={{ color: "var(--foreground)" }}>{value}</span>} />
            {series.map(([key, colour]) => (
              <Bar key={key} dataKey={key} name={t(`dashboard.series.${key}`)} fill={colour} radius={[3, 3, 0, 0]} maxBarSize={18} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <table className="sr-only">
        <caption>{t("dashboard.monthly")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("dashboard.month")}</th>
            {series.map(([key]) => <th key={key} scope="col">{t(`dashboard.series.${key}`)}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.month}>
              <th scope="row">{row.name}</th>
              {series.map(([key]) => <td key={key}>{row[key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
} // prettier-ignore

const STATUS_COLOURS: Record<string, string> = {
  DRAFT: "var(--input)",
  SUBMITTED: "var(--chart-1)",
  UNDER_REVIEW: "var(--chart-2)",
  RETURNED: "var(--chart-4)",
  APPROVED: "var(--chart-3)",
  REJECTED: "var(--chart-5)",
  REVOKED: "var(--chart-6)",
};

/** Applications by status: a ring, and the same figures as a list. */
export function StatusChart({ counts }: { counts: Dashboard["byStatus"] }) {
  const { t } = useTranslation();
  const total = counts.reduce((sum, item) => sum + item.count, 0);
  const data = counts.map((item) => ({
    ...item,
    name: t(`applicationStatus.${item.status}`),
    fill: STATUS_COLOURS[item.status] ?? "var(--chart-1)",
  }));
  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row">
      <div role="img" aria-label={t("dashboard.statusSummary", { total })} className="relative size-44 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="name" innerRadius="62%" outerRadius="100%" paddingAngle={2} stroke="none" isAnimationActive={false}>
            </Pie>
            <Tooltip {...TOOLTIP} />
          </PieChart>
        </ResponsiveContainer>
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold tabular-nums">{total}</span>
          <span className="text-xs text-muted-foreground">{t("dashboard.total")}</span>
        </div>
      </div>
      <ul className="flex w-full flex-col gap-1.5 text-sm">
        {data.map((item) => (
          <li key={item.status} className="flex items-center gap-2">
            <span aria-hidden="true" className="size-2.5 shrink-0 rounded-full" style={{ background: STATUS_COLOURS[item.status] }} />
            <span className="flex-1">{item.name}</span>
            <span className="font-medium tabular-nums">{item.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
} // prettier-ignore
